// Pure helpers for ShadowDraftPanel.jsx (docs/agent-draft-shadow.md), kept in
// a .js file so ui/test can run them under node --test.
import { htmlToPlainText, isRoutineNextStep, looksLikeHtml, SALES_HOLD_PATH_OPTIONS, invoiceUnavailableText } from '@cxhub/shared'

// --- The agent draft as the main draft (2026-10-21) ---------------------------
// docs/agent-draft-shadow.md, Primary intents. For an intent in
// AGENT_DRAFT_PRIMARY_INTENTS the suggestion's draft and order note may be the
// agent's (modelMeta.draftSource 'agent'). The admin comparison then shows the
// pipeline's draft and note, the ones not used, in the agent column's layout;
// when the pipeline's draft is shown (a fallback) it shows the agent's as
// before.
export const PIPELINE_NOT_USED_TITLE = 'Pipeline draft (not used)'
export const AGENT_DRAFT_TITLE = 'Agent draft'

// The draft the comparison column shows: the shadow row itself, or, when the
// agent's draft is the suggestion's, the pipeline's draft and note from the
// same row (plain text; an email draft's HTML is flattened for reading).
export function comparisonDraft(draft) {
  if (!draft || draft.draftSource !== 'agent') return draft ?? null
  const reply = String(draft.pipelineReply ?? '')
  return {
    id: draft.id,
    suggestionId: draft.suggestionId,
    conversationId: draft.conversationId,
    intent: draft.intent,
    status: 'ok',
    replyText: looksLikeHtml(reply) ? htmlToPlainText(reply) : reply,
    draftReply: null,
    orderNote: draft.pipelineNote ?? null,
    summary: null,
    nextSteps: [],
    toolCalls: [],
    usage: null,
    attachInvoices: false,
    invoiceCheck: null,
    invoiceClaimMismatch: false,
    // The comparison stays (2026-10-21, the independent investigator): what
    // code found different between the agent (whose draft is the main one)
    // and the pipeline, the agent's own asks and actions, what it read and
    // the cap it hit.
    differences: draft.differences ?? [],
    asks: draft.asks ?? [],
    actions: draft.actions ?? [],
    agentOrder: draft.agentOrder ?? null,
    sources: draft.sources ?? null,
    hitLimit: draft.hitLimit ?? null,
    // The agent's confidence (2026-10-23): its run, whose draft is shown.
    confidence: draft.confidence ?? null,
    pipelineNotUsed: true,
    // The pipeline draft's own review reasons (2026-10-21): shown here, not
    // on the suggestion, since its draft is not the one shown.
    reviewReason: draft.pipelineReviewReason ?? null,
    // The agent's own steps shown (2026-10-30, migration 0169): the steps
    // on the main panel are the agent's, so this column lists the
    // pipeline's steps, not used and never processed, instead of the
    // agent's step previews.
    stepsShownAgent: draft.stepsSource === 'agent',
    pipelineSteps: draft.stepsSource === 'agent' ? pipelineStepsNotUsed(draft.pipelineSteps) : [],
  }
}

// The pipeline's steps not used (2026-10-30): { label, system } per step,
// in its order; a step with no label is named by its type.
export const PIPELINE_STEPS_NOT_USED_TITLE = 'Pipeline steps (not used, never processed)'
export function pipelineStepsNotUsed(steps) {
  return (Array.isArray(steps) ? steps : []).filter((s) => s?.type).map((s) => ({
    label: String(s.label ?? '').trim() || String(s.type).replace(/_/g, ' '),
    system: s.targetSystem ?? null,
  }))
}

// The agent's confidence (2026-10-23, server/src/ai/agent-confidence.js):
// { label: "Confidence: High", level, reasons: "a · b", alwaysShowReasons }
// or null for a run without one. Low always shows its reasons.
const CONFIDENCE_WORDS = { high: 'High', medium: 'Medium', low: 'Low' }
export function confidenceView(confidence, { agentPrefix = false } = {}) {
  const level = confidence?.level
  if (!CONFIDENCE_WORDS[level]) return null
  const reasons = (confidence.reasons ?? []).filter((r) => typeof r === 'string' && r.trim()).join(' · ')
  return { level, label: `${agentPrefix ? 'Agent confidence' : 'Confidence'}: ${CONFIDENCE_WORDS[level]}`, reasons, alwaysShowReasons: level === 'low' }
}

// The column's title: "Pipeline draft (not used)" or "Agent draft".
export function comparisonTitle(draft) {
  return draft?.pipelineNotUsed ? PIPELINE_NOT_USED_TITLE : AGENT_DRAFT_TITLE
}

// The agent's What's next on the main panel (modelMeta.agentNextSteps), only
// when the agent's draft is the suggestion's; the routine steps hidden, as in
// the agent column.
export function agentNextStepsFor(suggestion) {
  if (suggestion?.modelMeta?.draftSource !== 'agent') return []
  return (suggestion.modelMeta.agentNextSteps ?? []).filter((s) => String(s ?? '').trim() && !isRoutineNextStep(s))
}

// "Agent draft: needs a person" (2026-10-21): the server's hold on an
// agent-drafted reply whose flow is set to Automate, else null.
export function agentAutomationHold(suggestion) {
  return suggestion?.modelMeta?.flowMatch?.automationHold ?? null
}

// The placeholder warning under an agent reply (2026-10-19) is one line
// since 2026-10-23: agentDraftLayout.js's placeholderLine().

// The shadow draft for one suggestion, or null.
export function shadowDraftFor(shadowDrafts, suggestionId) {
  if (!suggestionId) return null
  return (shadowDrafts ?? []).find((d) => d.suggestionId === suggestionId) ?? null
}

// The English boxes under the agent's reply and order note (2026-10-19): the
// Translate button's shadowDraft ({ suggestionId, reply, orderNote }), only
// while Translate is on and only for this draft's own suggestion. null when
// there is nothing to show.
// Since 2026-10-20 also the summary and the What's next steps (an array, one
// per step), for drafts written before the agent kept them in English.
// Since 2026-10-27 the summary, What's next and the note are stored in
// English (server/src/ai/internal-english.js): an English box that only
// repeats the stored text is not shown. Older rows still get theirs.
export function shadowTranslationFor(draft, showTranslation, translation) {
  if (!showTranslation || !draft || !translation) return null
  if (translation.suggestionId !== draft.suggestionId) return null
  const same = (a, b) => String(a ?? '').trim() === String(b ?? '').trim()
  const differing = (en, own) => (en && !same(en, own) ? en : null)
  const steps = Array.isArray(translation.nextSteps) ? translation.nextSteps : null
  const ownSteps = draft.nextSteps ?? []
  const nextSteps = steps && steps.some((en, i) => en && !same(en, ownSteps[i])) ? steps : null
  const orderNote = differing(translation.orderNote, draft.orderNote)
  const summary = differing(translation.summary, draft.summary)
  if (!translation.reply && !orderNote && !summary && !nextSteps) return null
  return { reply: translation.reply || null, orderNote, summary, nextSteps }
}

// The text to show and copy: the reply with real values put back, else the
// redacted draft (a row from before reply_text existed).
// The left column's label when the contact has no main AI draft (2026-10-20;
// a rule-based path handled it, so only the agent draft has a reply): "No AI
// draft: <reason>", the first sentence of the review reason, else the first
// proposed step's label. null when there is a draft.
export const NO_AI_DRAFT_REASON_MAX = 120
export function noAiDraftLabel(suggestion) {
  if (!suggestion || String(suggestion.draftReply ?? '').trim()) return null
  const firstSentence = String(suggestion.humanReviewReason ?? '').trim().split(/(?<=[.!?])\s+/)[0]
  const step = (suggestion.proposedActions ?? []).find((a) => a?.label)?.label ?? null
  const reason = firstSentence || step || 'a rule-based path handled this contact'
  const short = reason.length > NO_AI_DRAFT_REASON_MAX ? `${reason.slice(0, NO_AI_DRAFT_REASON_MAX - 1).trimEnd()}…` : reason
  return `No AI draft: ${short}`
}

export function shadowReplyText(draft) {
  return draft?.replyText || draft?.draftReply || ''
}

// One line per tool call, without the model rounds.
// get_invoices says what it found (2026-10-19): "1 invoice (INV1)" or "none,
// <reason>", from the trace entry's available / invoiceIds / reason.
export function toolCallLines(toolCalls) {
  return (toolCalls ?? [])
    .filter((c) => c.tool && c.tool !== 'model' && c.tool !== 'limit')
    .flatMap((c) => {
      // A line code removed as a repeat (2026-10-28, the agent's own first
      // sentence already said it): "Removed a repeated line: "..."".
      if (c.tool === 'removed_repeated_line' && Array.isArray(c.lines)) return c.lines.map((l) => `Removed a repeated line: "${l}"`)
      // A content piece the reply leaves out (2026-10-29): template text is
      // listed here for review, never in What's next; a data piece is also a
      // What's next line.
      // A stage reply of the agent's that left out a data piece of its
      // template (2026-10-29): the filled template is used instead.
      if (c.tool === 'stage_replies' && c.fallbacks) return Object.entries(c.fallbacks).map(([stage, pieces]) => `The agent's ${stage === 'in_transit' ? 'in transit' : stage} reply left out ${(pieces ?? []).join(', ')}; the filled template is used`)
      // Since 2026-10-30 these are the trace only, never What's next: a data
      // piece code added back says so (filled, or with its placeholder,
      // which What's next asks to fill in); one of a template the return
      // step does not call for was not added.
      if (c.tool === 'content_pieces' && Array.isArray(c.pieces)) return c.pieces.map(contentPieceLine)
      // The reply rebuilt from the return step's template (2026-10-30).
      if (c.tool === 'reply_rebuilt') return [c.ok ? `Reply rebuilt from "${c.template}" by code${c.from ? ` (the agent followed "${c.from}")` : ''}` : `Reply not rebuilt from "${c.template}": its template could not be read`]
      return [toolCallLine(c)]
    })
}

// One content piece of the trace (2026-10-30).
export function contentPieceLine(p) {
  const from = p?.title ? ` from "${p.title}"` : ''
  if (p?.added === 'filled') return `Added back ${p.piece}${from}, filled`
  if (p?.added === 'placeholder') return `Added back ${p.piece}${from} with its placeholder, to fill in`
  if (p?.notThisTemplate) return `Not added: ${p.piece}${from} (the return step calls for another template)`
  return `The reply leaves out ${p?.piece}${from}${p?.data ? '' : ' (template text: read the reply before sending)'}`
}

function toolCallLine(c) {
  // The argument that says what was looked up (2026-10-21: the toolkit's
  // order, SKU, flow, template or reference).
  const a = c.args ?? {}
  const shown = a.tracking_number ?? a.sku ?? a.flowKey ?? a.id ?? a.query ?? a.order ?? a.document ?? a.by ?? null
  const arg = shown != null && shown !== '' ? ` ${shown}` : ''
  let result = c.ok ? 'ok' : `failed${c.error ? ` (${c.error})` : ''}`
  if (c.tool === 'get_invoices' && c.ok && typeof c.available === 'boolean') {
    const ids = c.invoiceIds ?? []
    result = c.available && ids.length
      ? `${ids.length} invoice${ids.length === 1 ? '' : 's'} (${ids.join(', ')})`
      : `none${c.reason ? `, ${c.reason}` : ''}`
  }
  return `${c.tool}${arg}: ${result}, ${c.durationMs ?? 0} ms`
}

// The invoice ids get_invoices found for this draft, in order, once each.
export function shadowInvoiceIds(draft) {
  const ids = []
  for (const c of draft?.toolCalls ?? []) {
    if (c?.tool !== 'get_invoices') continue
    for (const id of c.invoiceIds ?? []) if (id && !ids.includes(id)) ids.push(id)
  }
  return ids
}

// One invoice's data: URL from GET /:id/invoice-attachments' { attachments },
// or null.
export function invoiceDataUrlFor(result, invoiceId) {
  return (result?.attachments ?? []).find((a) => a?.invoiceId === invoiceId)?.dataUrl ?? null
}

// Why "Open INV…" cannot open an invoice (2026-10-29): the preload's reason
// when it attached nothing (the same words as the reply box), else the
// invoice is not among those it rendered.
export function invoiceOpenErrorText(result, invoiceId) {
  if (!(result?.attachments ?? []).length && result?.reason) return invoiceUnavailableText(result.reason)
  return `${invoiceId} was not found in the order's invoices`
}

// The agent attached invoices but the preload renders none of them
// (2026-10-29): the line the Invoices box shows instead of "Attached to the
// reply", so the two never contradict. null while unknown (not loaded) or
// when any of the agent's invoices is there.
export function invoiceNotAttachableLine(draft, result) {
  if (!draft?.attachInvoices || !result) return null
  const ids = shadowInvoiceIds(draft)
  const got = (result.attachments ?? []).map((a) => a?.invoiceId)
  if (ids.length ? ids.some((id) => got.includes(id)) : got.length > 0) return null
  return `Checked by the agent, but it can't be attached: ${result.reason ? invoiceUnavailableText(result.reason) : (ids.length ? `${ids.join(', ')} ${ids.length > 1 ? 'are' : 'is'} not among the order's invoices.` : 'no invoice could be rendered.')}`
}

// A data: URL as a Blob, so it opens in a new tab as a blob: URL (browsers
// block top-level data: URLs). null for anything that is not base64 data.
export function dataUrlToBlob(dataUrl) {
  const m = /^data:([^;,]+);base64,(.*)$/s.exec(String(dataUrl ?? ''))
  if (!m) return null
  const bin = atob(m[2])
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
  return new Blob([bytes], { type: m[1] })
}

// --- Side-by-side comparison (2026-10-16) -------------------------------------
// docs/decisions/2026-10-16-shadow-draft-side-by-side.md. For an admin (not in
// View as Agent), the current suggestion and the agent's shadow draft show as
// two columns; collapsible, the choice remembered per browser.

export const SHADOW_COMPARE_STORAGE_KEY = 'cxhub.shadowCompare.collapsed'
// The agent-draft column's width; the AI column grows by this much while the
// comparison is shown side by side and expanded.
export const SHADOW_COMPARE_COLUMN_WIDTH = 340

// The agent-draft column is resizable (2026-10-19): SHADOW_COMPARE_COLUMN_WIDTH
// is its default, these its limits. The AI suggestion's own limits are App.jsx's
// useResizableWidth('suggestionPanel', ...) ones.
export const SHADOW_COLUMN_MIN_WIDTH = 260
export const SHADOW_COLUMN_MAX_WIDTH = 720
export const SUGGESTION_COLUMN_MIN_WIDTH = 360
// The messages column never gets narrower than this.
export const MESSAGES_COLUMN_MIN_WIDTH = 320
// The resize handles' width (ResizablePane.jsx's ResizeHandle).
export const RESIZE_HANDLE_WIDTH = 8

// The two AI widths as shown, never wider than the screen (2026-10-19). rowWidth:
// the thread row (messages + handles + AI column); null when not measured yet
// (the widths are used as they are). Side by side, the agent draft shrinks
// first, then the suggestion, each down to its minimum; the messages column
// keeps MESSAGES_COLUMN_MIN_WIDTH while they can still shrink.
export function clampAiColumnWidths({ rowWidth = null, suggestionWidth, shadowWidth, side = false }) {
  let suggestion = suggestionWidth
  let shadow = side ? shadowWidth : 0
  if (!(rowWidth > 0)) return { suggestion, shadow }
  const handles = RESIZE_HANDLE_WIDTH * (side ? 2 : 1)
  const available = rowWidth - MESSAGES_COLUMN_MIN_WIDTH - handles
  let excess = suggestion + shadow - available
  if (excess > 0 && side) {
    const take = Math.min(excess, Math.max(0, shadow - SHADOW_COLUMN_MIN_WIDTH))
    shadow -= take
    excess -= take
  }
  if (excess > 0) {
    const take = Math.min(excess, Math.max(0, suggestion - SUGGESTION_COLUMN_MIN_WIDTH))
    suggestion -= take
  }
  return { suggestion, shadow }
}
// Below this viewport width the agent draft stacks under the current one.
// 1500, not 1100 (2026-10-16, Jenn): side by side, the messages column was
// 264 px on a 1366 px laptop and 338 px on a 1440 px one (default pane
// widths), too narrow to read; at 1536 px it is 434 px.
export const SHADOW_COMPARE_MIN_VIEWPORT = 1500

// Whether the comparison is collapsed. Storage that is missing or throws
// (private mode, blocked site data) reads as shown.
export function readShadowCompareCollapsed(storage) {
  try {
    return storage?.getItem(SHADOW_COMPARE_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

// Remembers the choice; a storage failure is ignored (the choice lasts the
// session only).
export function writeShadowCompareCollapsed(storage, collapsed) {
  try {
    storage?.setItem(SHADOW_COMPARE_STORAGE_KEY, collapsed ? '1' : '0')
  } catch { /* not remembered */ }
}

// How the comparison shows: null (nothing: not enabled, or no shadow draft for
// this suggestion), 'side' (two columns), 'rail' (collapsed to a thin rail
// beside the current one), 'stacked' (under the current one, narrow viewport)
// or 'stacked-collapsed' (its header only).
export function shadowCompareMode({ enabled, draft, wide, collapsed }) {
  if (!enabled || !draft) return null
  if (!wide) return collapsed ? 'stacked-collapsed' : 'stacked'
  return collapsed ? 'rail' : 'side'
}

// --- The independent agent (2026-10-21) --------------------------------------
// docs/decisions/2026-10-21-shadow-agent-independent-toolkit.md. The agent
// lists its own asks and actions; code compares them with the pipeline and
// stores the differences on the draft. Older drafts have none of these.

// The differences, one line each ("Agent: replacement 1AECI00383 × 1.
// Pipeline: 1AERK01853 × 1 (whole kit).").
export function shadowDifferenceLines(draft) {
  return (draft?.differences ?? []).map((d) => (typeof d?.text === 'string' ? d.text.trim() : '')).filter(Boolean)
}

// The agent's actions, one line each: its own sentence, else the type, the
// items and the reason code. Never run; for the person and the comparison.
export function shadowActionLines(draft) {
  return (draft?.actions ?? []).map((a) => {
    if (typeof a?.text === 'string' && a.text.trim()) return a.text.trim()
    const items = (a?.items ?? []).filter((i) => i?.sku).map((i) => `${i.sku} × ${i.quantity ?? 1}`).join(', ')
    return [a?.type ?? 'other', items, a?.reasonCode != null ? `reason ${a.reasonCode}` : null].filter(Boolean).join(', ')
  }).filter(Boolean)
}

// What the agent read, for the column's footer: "Order ORD1. Flows:
// product_defect. Templates: t-70." null when there is nothing to say.
export function shadowSourcesLine(draft) {
  const parts = []
  if (draft?.agentOrder) parts.push(`Order ${draft.agentOrder}.`)
  const flows = draft?.sources?.flows ?? []
  const templates = draft?.sources?.templates ?? []
  if (flows.length) parts.push(`Flows read: ${flows.join(', ')}.`)
  if (templates.length) parts.push(`Templates read: ${templates.join(', ')}.`)
  return parts.length ? parts.join(' ') : null
}

// The cap the run reached, as a warning, or null.
export function shadowLimitWarning(draft) {
  if (draft?.hitLimit === 'rounds') return 'The agent reached its limit of tool calls; What\'s next lists what it could not check.'
  if (draft?.hitLimit === 'time') return 'The agent reached its time limit; What\'s next lists what it could not check.'
  return null
}

// --- Steps as previews (2026-10-23) ------------------------------------------
// docs/agent-draft-shadow.md, Steps as previews. The server builds each of the
// agent's actions as the pipeline's step (ai/shadow-step-previews.js) and
// sends them as draft.stepPreviews; an admin's edits are saved on the shadow
// draft (PUT .../shadow-drafts/:shadowId/steps/:key). Display only.

// The line at the bottom of the agent column, in place of buttons.
export const PREVIEW_ONLY_TEXT = 'Preview only. Nothing here is processed or sent.'

// The main panel's one button as a preview (2026-10-27, Jenn's spec "Agent
// drafts on eBay cases, inquiries and returns"): the same label the CSR
// would get ("Process 2 steps and send reply", "Process 1 step", "Send
// Reply"), shown disabled with "Preview only". The steps it counts are the
// checked ones Process all would run: a built step with a system (Console or
// eBay), never a message step (the reply is the message), advice or a part.
const NOT_PROCESSED_KINDS = new Set(['message', 'advice', 'part'])
export function shadowProcessableCount(steps = [], unchecked = []) {
  return (steps ?? []).filter((p, i) => !(unchecked ?? []).includes(i) && p?.built && p?.system && !NOT_PROCESSED_KINDS.has(p.kind)).length
}
export const SHADOW_BUTTON_PREVIEW_LINE = 'Preview only'
// How long the column waits after the last change before saving a step.
export const STEP_SAVE_DELAY_MS = 600

// The rows of the Steps section. With the pipeline's draft not used
// (pipelineNotUsed), its order note, read only. A draft from a server that
// sends no previews (stepPreviews missing): the staged note, read only.
export function shadowStepRows(draft) {
  if (!draft) return []
  const note = typeof draft.orderNote === 'string' && draft.orderNote.trim() ? draft.orderNote : null
  const noteRow = (title) => ({ key: 'note', kind: 'note', type: 'add_order_note', readOnly: true, title, system: 'Console', fields: [], values: { note }, call: null, callNote: null, warnings: [] })
  if (draft.pipelineNotUsed) return note ? [noteRow('Order note')] : []
  if (Array.isArray(draft.stepPreviews)) return draft.stepPreviews
  return note ? [noteRow('Add note to order')] : []
}

// The values one step saves: only its own fields.
export function stepEditValues(preview, values) {
  const out = {}
  for (const f of preview?.fields ?? []) if (values?.[f] !== undefined) out[f] = values[f]
  return out
}

// A refund step's Full / Partial switch (2026-10-26, every step builds): the
// comment follows the type, as on the main panel's Issue Refund (its
// RefundModal.jsx comments); Full sets the amount to the order total, Partial
// leaves the amount for a person.
export function refundTypeChange(type, preview, comments = {}) {
  const max = preview?.options?.maxAmount ?? null
  return { refundType: type, comment: comments[type] ?? '', amount: type === 'full' ? max : null }
}

// "Edited 10/26/2026, 10:42 AM" for a step's saved edit; null without one.
export function editedAtLabel(at) {
  if (!at) return null
  const d = new Date(at)
  if (Number.isNaN(d.getTime())) return null
  return `Edited ${d.toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit' })}`
}

// Whether a step preview can be edited: it has fields and the draft it
// belongs to has an id.
export function stepIsEditable(preview, draft) {
  return Boolean(!preview?.readOnly && (preview?.fields ?? []).length && draft?.id && draft?.conversationId)
}

// The reply's header rows (From / To / CC / Subject) as the reply box would
// use them, for an email reply; [] otherwise (an eBay message has none).
export function replyHeaderRows(headers) {
  if (!headers) return []
  return [['From', headers.from], ['To', headers.to], ['CC', headers.cc], ['Subject', headers.subject]]
    .filter(([label, v]) => label !== 'CC' || v != null)
    .map(([label, v]) => [label, String(v ?? '')])
}

// The sales-hold path choice (2026-10-27; docs/agent-draft-shadow.md): the
// agent's own preselection on a branch that can go either way, from its
// trace (sales_hold_path). { path, options } or null. The panel shows the
// same choice as the main panel; nothing is processed here.
export function shadowSalesHoldPath(draft) {
  const entry = [...(draft?.toolCalls ?? [])].reverse().find((c) => c?.tool === 'sales_hold_path')
  if (entry?.path !== 'fix' && entry?.path !== 'contact') return null
  return { path: entry.path, options: SALES_HOLD_PATH_OPTIONS.map((o) => ({ ...o })) }
}
